import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, MapPin, DollarSign, Users, FileText, User, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

// Import the same tags from Contacts
const AVAILABLE_TAGS = [
  { value: 'artist', label: 'Artist', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' },
  { value: 'venue', label: 'Venue', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  { value: 'promoter', label: 'Promoter', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  { value: 'booking', label: 'Booking Agent', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
  { value: 'tech', label: 'Tech/Sound', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' },
  { value: 'merch', label: 'Merch', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400' },
  { value: 'press', label: 'Press/Media', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400' },
];

const TimelineEditModal = ({ show, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    venue: '',
    city: '',
    state: '',
    date: '',
    loadIn: '',
    soundcheck: '',
    doors: '',
    showtime: '',
    guarantee: '',
    expectedPayout: '',
    actualPayout: '',
    attendance: '',
    notes: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactTags: []
  });

  useEffect(() => {
    if (show) {
      setFormData({
        venue: show.venue || '',
        city: show.city || '',
        state: show.state || '',
        date: show.date || '',
        loadIn: show.loadIn || '',
        soundcheck: show.soundcheck || '',
        doors: show.doors || '',
        showtime: show.showtime || '',
        guarantee: show.guarantee || '',
        expectedPayout: show.expectedPayout || '',
        actualPayout: show.actualPayout || '',
        attendance: show.attendance || '',
        notes: show.notes || '',
        contactName: show.contactName || '',
        contactEmail: show.contactEmail || '',
        contactPhone: show.contactPhone || '',
        contactTags: show.contactTags || []
      });
    }
  }, [show]);

  if (!isOpen || !show) return null;

  const handleSave = () => {
    if (!formData.venue || !formData.date) {
      toast.error('Venue and Date are required');
      return;
    }
    
    onUpdate(show.dateKey, show.id, formData);  
    onClose();
    toast.success('Show updated successfully!');
  };

  const handleCancel = () => {
    setFormData({
      venue: show.venue || '',
      city: show.city || '',
      state: show.state || '',
      date: show.date || '',
      loadIn: show.loadIn || '',
      soundcheck: show.soundcheck || '',
      doors: show.doors || '',
      showtime: show.showtime || '',
      guarantee: show.guarantee || '',
      expectedPayout: show.expectedPayout || '',
      actualPayout: show.actualPayout || '',
      attendance: show.attendance || '',
      notes: show.notes || '',
      contactName: show.contactName || '',
      contactEmail: show.contactEmail || '',
      contactPhone: show.contactPhone || '',
      contactTags: show.contactTags || []
    });
    onClose();
  };

  const toggleContactTag = (tagValue) => {
    setFormData(prev => ({
      ...prev,
      contactTags: prev.contactTags.includes(tagValue)
        ? prev.contactTags.filter(t => t !== tagValue)
        : [...prev.contactTags, tagValue]
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
      <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Edit Show</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{show.venue} - {show.date}</p>
          </div>
          <button
            onClick={handleCancel}
            className="btn-press p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <MapPin size={20} className="text-blue-600" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Venue *
                  </label>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Schedule Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Calendar size={20} className="text-green-600" />
                Schedule
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Load In
                  </label>
                  <input
                    type="time"
                    value={formData.loadIn}
                    onChange={(e) => setFormData({ ...formData, loadIn: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Soundcheck
                  </label>
                  <input
                    type="time"
                    value={formData.soundcheck}
                    onChange={(e) => setFormData({ ...formData, soundcheck: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Doors
                  </label>
                  <input
                    type="time"
                    value={formData.doors}
                    onChange={(e) => setFormData({ ...formData, doors: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Showtime
                  </label>
                  <input
                    type="time"
                    value={formData.showtime}
                    onChange={(e) => setFormData({ ...formData, showtime: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Financial Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <DollarSign size={20} className="text-yellow-600" />
                Financial
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Guarantee
                  </label>
                  <input
                    type="number"
                    value={formData.guarantee}
                    onChange={(e) => setFormData({ ...formData, guarantee: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Expected Payout
                  </label>
                  <input
                    type="number"
                    value={formData.expectedPayout}
                    onChange={(e) => setFormData({ ...formData, expectedPayout: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Actual Payout
                  </label>
                  <input
                    type="number"
                    value={formData.actualPayout}
                    onChange={(e) => setFormData({ ...formData, actualPayout: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Attendance Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Users size={20} className="text-purple-600" />
                Attendance
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Attendance
                </label>
                <input
                  type="number"
                  value={formData.attendance}
                  onChange={(e) => setFormData({ ...formData, attendance: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <User size={20} className="text-indigo-600" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Contact Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Tag size={16} />
                  Contact Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_TAGS.map(tag => (
                    <button
                      key={tag.value}
                      onClick={() => toggleContactTag(tag.value)}
                      className={`btn-press px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                        formData.contactTags.includes(tag.value)
                          ? tag.color + ' ring-2 ring-blue-500 scale-105'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:scale-105'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  💡 These tags help categorize the contact when they appear in the Contacts page
                </p>
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <FileText size={20} className="text-orange-600" />
                Notes
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="btn-press px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn-press flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Save size={20} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelineEditModal;